import React, { useCallback } from 'react';
import { Check, GraduationCap, Shuffle, User, Users } from 'lucide-react';
import StepHeading from './StepHeading';
import { noAutofill } from '../noAutofill';

interface BuildCommercialStep4Props {
  character: string;
  setCharacter: (value: string) => void;
  customCharacter: string;
  setCustomCharacter: (value: string) => void;
  characterDescription?: string;
  loading: boolean;
  onCharacterChange?: (characterId: string, description: string) => void;
}

const CAST = [
  {
    id: 'solo-star',
    label: 'Solo Star',
    icon: <User />,
    prompt: 'A [adjective] person who [loves / needs / discovers]…',
    example: 'An exhausted parent who desperately needs energy',
  },
  {
    id: 'dynamic-duo',
    label: 'Dynamic Duo',
    icon: <Users />,
    prompt: 'Two [relationship] who [are trying to / want to]…',
    example: 'Two coworkers who compete over everything',
  },
  {
    id: 'the-expert',
    label: 'The Expert',
    icon: <GraduationCap />,
    prompt: 'A [profession] demonstrating…',
    example: 'A scientist explaining the breakthrough',
  },
  {
    id: 'wild-card',
    label: 'Wild Card',
    icon: <Shuffle />,
    prompt: 'Write your own',
    example: '',
  },
];

const BuildCommercialStep4: React.FC<BuildCommercialStep4Props> = React.memo(
  ({ character, setCharacter, customCharacter, setCustomCharacter, characterDescription = '', onCharacterChange }) => {
    const [descriptions, setDescriptions] = React.useState<Record<string, string>>({});

    const toggle = useCallback(
      (id: string) => setCharacter(character === id ? '' : id),
      [character, setCharacter]
    );

    const describe = useCallback(
      (id: string, value: string) => {
        if (id === 'wild-card') {
          setCustomCharacter(value);
        } else {
          setDescriptions((prev) => ({ ...prev, [id]: value }));
        }
        onCharacterChange?.(id, value);
      },
      [setCustomCharacter, onCharacterChange]
    );

    const valueFor = (id: string) => {
      if (id === 'wild-card') return customCharacter || '';
      if (id === character && !descriptions[id] && characterDescription) {
        return characterDescription;
      }
      return descriptions[id] || '';
    };

    return (
      <div className="ed-enter">
        <StepHeading
          index={4}
          kicker="Casting"
          title="Someone has to carry this."
          lede="Choose a shape for your cast, then write them in your own words. Specific people are far more watchable than archetypes."
        />

        <div className="ed-block">
          <div className="ed-grid ed-grid--2" role="radiogroup" aria-label="Cast">
            {CAST.map((option) => {
              const isSelected = character === option.id;

              return (
                <div
                  key={option.id}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={0}
                  className={`ed-card${isSelected ? ' is-selected' : ''}`}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).tagName === 'INPUT') return;
                    toggle(option.id);
                  }}
                  onKeyDown={(e) => {
                    if ((e.target as HTMLElement).tagName === 'INPUT') return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(option.id);
                    }
                  }}
                >
                  {isSelected && (
                    <span className="ed-mark" aria-hidden="true">
                      <Check />
                    </span>
                  )}

                  <span className="ed-card-icon" aria-hidden="true">
                    {option.icon}
                  </span>
                  <h3 className="ed-card-title">{option.label}</h3>

                  <div className="ed-card-extra">
                    <input
                      type="text"
                      className="ed-field-boxed"
                      placeholder={option.prompt}
                      value={valueFor(option.id)}
                      onChange={(e) => describe(option.id, e.target.value)}
                      onFocus={() => {
                        if (character !== option.id) setCharacter(option.id);
                      }}
                      aria-label={`${option.label} description`}
                      {...noAutofill}
                    />
                    {option.example && <p className="ed-hint">e.g. {option.example}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);

BuildCommercialStep4.displayName = 'BuildCommercialStep4';

export default BuildCommercialStep4;
